/**
 * One-carbon (B12 / homocysteine) pattern resolution for the Vitamins &
 * Minerals section. Pure functions over a marker-state object, kept free of
 * BloodworkAnalysis so they unit-test standalone — the same split
 * `resolveThyroidPattern` and `adrenalGauge` already use.
 *
 * WHY THIS SECTION HAS PATTERNS AT ALL
 * Glucose and Thyroid are axes: closed feedback loops where marker A is only
 * interpretable against marker B. Vitamins & minerals is a portfolio of mostly
 * independent status readouts, so a ranked list of bars covers most of it. The
 * patterns below exist for the cases where the BARS ACTIVELY MISLEAD:
 *   • serum B12 optimal while homocysteine says it is not reaching cells
 *   • serum folate high while RBC folate says stored status is low
 *   • folate holding red cell size normal while B12 keeps falling
 * A green bar is wrong in every one of those, and no amount of sorting fixes it.
 *
 * DETECTION IS NOT THE SHEET'S LOGIC COLUMN. The Logic cell is the marker-arrow
 * row the panel DISPLAYS; the rules here decide what fires. Detection is
 * deliberately allowed to cover more states than Logic advertises (same house
 * rule as the Glucose add-ons), so do not "fix" a mismatch by tightening these.
 *
 * MCV, MCH, RDW and eGFR are BORROWED from the RBC and Kidney sections. They
 * are read but never displayed here. This matters more than it sounds: the CBC
 * is ordered for practically everyone, so the B12 story always has at least two
 * markers even when B12 is the only nutrient on the requisition.
 */

/** A marker's read for this test. `absent` means it was never measured. */
export type NutrientState = 'low' | 'optimal' | 'high' | 'absent';

export interface NutrientMarkerStates {
  vitaminB12: NutrientState;
  vitaminB6: NutrientState;
  folateSerum: NutrientState;
  folateRBC: NutrientState;
  homocysteine: NutrientState;
  mcv: NutrientState;
  rdw: NutrientState;
  eGFR: NutrientState;
}

// categoryId block: 87100–87145 patterns, 87150–87175 open questions.
// (8000–8330 stays the existing per-marker nutrient root causes.)
export const ONE_CARBON = {
  B12_LOW_MACROCYTIC: 87100,
  B12_LOW_NORMOCYTIC: 87105,
  B12_RANGE_HCY_HIGH: 87110,
  COFACTOR_LOW_HCY_HIGH: 87115,
  HCY_HIGH_COFACTORS_OK: 87120,
  FOLATE_SERUM_OVER_RBC: 87125,
  FOLATE_MASKING_B12: 87130,
  B12_HIGH: 87135,
  HCY_LOW_B_HIGH: 87140,
  MACROCYTIC_NO_B_CAUSE: 87145,
} as const;

export const OPEN_QUESTION = {
  B12_USE_UNASSESSED: 87150,
  MACROCYTIC_B12_USE_UNASSESSED: 87155,
  B6_NOT_MEASURED: 87160,
  RBC_FOLATE_NOT_MEASURED: 87165,
  EGFR_NOT_MEASURED: 87170,
  FOLATE_NOT_MEASURED: 87175,
} as const;

const measured = (s: NutrientState): boolean => s !== 'absent';
/** Either folate compartment reading low. Either one is enough to call it low. */
const folateLow = (s: NutrientMarkerStates): boolean =>
  s.folateSerum === 'low' || s.folateRBC === 'low';
/** True only when a folate compartment was measured AND is not low. */
const folateOkMeasured = (s: NutrientMarkerStates): boolean =>
  (s.folateSerum === 'optimal' || s.folateSerum === 'high' ||
    s.folateRBC === 'optimal' || s.folateRBC === 'high') && !folateLow(s);

/**
 * Every one-carbon pattern this test satisfies, in categoryId order.
 *
 * Multiple patterns can fire at once and that is intended — 2.8 (an elevated
 * B12 level) and 2.3 (that B12 not reaching cells) are both true and say
 * different things. Only genuine subsumption is suppressed, noted at each site.
 */
export function resolveOneCarbonPatterns(s: NutrientMarkerStates): number[] {
  const out: number[] = [];
  const hcyHigh = s.homocysteine === 'high';
  const b12InRange = s.vitaminB12 === 'optimal' || s.vitaminB12 === 'high';

  /*
   * 2.5 vs 2.3 — both read "homocysteine high, B12 not low", and the ONLY thing
   * that separates them is how much was measured. 2.5 claims the B vitamins are
   * not the explanation, which is a claim you can only make when all three
   * cofactors were actually measured and all sit in range. Anything less and the
   * honest reading is 2.3 (B12 present but possibly not usable). Computed first
   * so 2.3 can stand down.
   */
  const cofactorsAllMeasuredAndOk =
    hcyHigh &&
    s.vitaminB12 === 'optimal' &&
    s.vitaminB6 === 'optimal' &&
    folateOkMeasured(s);

  /*
   * 2.1 — low B12 that has already reached the marrow.
   *
   * MCV ONLY. MCH was dropped 2026-08-21: it is MCV restated, because a bigger
   * cell carries proportionally more hemoglobin, so it added a marker without
   * adding information.
   *
   * MCHC is deliberately NOT used either. It is the chromia marker (hemoglobin
   * concentration relative to cell size, arithmetically MCH/MCV), but B12
   * deficiency is macrocytic NORMOchromic — volume and hemoglobin rise together
   * and MCHC does not move. A genuinely high MCHC points at spherocytosis or an
   * assay artifact (cold agglutinins, lipemia), so keying this pattern off it
   * would either never fire or fire on the wrong condition.
   */
  if (s.vitaminB12 === 'low' && s.mcv === 'high') {
    out.push(ONE_CARBON.B12_LOW_MACROCYTIC);
  }

  /*
   * 2.7 — folate holding red cell size normal while B12 stays low. Subsumes 2.2
   * (it is the same low B12 with an unchanged MCV, plus the reason), so 2.2
   * stands down when this fires.
   */
  const folateMaskingB12 =
    s.vitaminB12 === 'low' &&
    (s.folateSerum === 'optimal' || s.folateSerum === 'high') &&
    s.mcv !== 'high';

  /*
   * 2.2 — low B12 before the marrow shows it. Requires MCV to have been
   * MEASURED and not enlarged: with no CBC there is nothing to call "unchanged",
   * and the low B12 still reaches the client as a bar and a root-cause row.
   */
  if (
    s.vitaminB12 === 'low' &&
    (s.mcv === 'optimal' || s.mcv === 'low') &&
    !folateMaskingB12
  ) {
    out.push(ONE_CARBON.B12_LOW_NORMOCYTIC);
  }

  // 2.3 — the headline. B12 in the blood but the clearance pathway says it is
  // not being used. Held back when B6 or folate is low (2.4 explains it better)
  // and when everything was measured and is fine (2.5 explains it better).
  if (
    hcyHigh &&
    b12InRange &&
    s.vitaminB6 !== 'low' &&
    !folateLow(s) &&
    !cofactorsAllMeasuredAndOk
  ) {
    out.push(ONE_CARBON.B12_RANGE_HCY_HIGH);
  }

  // 2.4 — a named cofactor is low. The box says which.
  if (hcyHigh && (s.vitaminB12 === 'low' || folateLow(s) || s.vitaminB6 === 'low')) {
    out.push(ONE_CARBON.COFACTOR_LOW_HCY_HIGH);
  }

  if (cofactorsAllMeasuredAndOk) out.push(ONE_CARBON.HCY_HIGH_COFACTORS_OK);

  /*
   * 2.6 — serum folate running ahead of the stored level. Needs RBC folate to be
   * measured and NOT high; two high readings are simply high folate, not a
   * discordance.
   */
  if (
    s.folateSerum === 'high' &&
    (s.folateRBC === 'low' || s.folateRBC === 'optimal')
  ) {
    out.push(ONE_CARBON.FOLATE_SERUM_OVER_RBC);
  }

  if (folateMaskingB12) out.push(ONE_CARBON.FOLATE_MASKING_B12);

  if (s.vitaminB12 === 'high') out.push(ONE_CARBON.B12_HIGH);

  if (
    s.homocysteine === 'low' &&
    (s.vitaminB12 === 'high' || s.folateSerum === 'high')
  ) {
    out.push(ONE_CARBON.HCY_LOW_B_HIGH);
  }

  // 2.10 — enlarged cells with the B vitamins ruled out. Never alongside 2.1,
  // which owns the low-B12 version of a high MCV.
  if (
    s.mcv === 'high' &&
    s.vitaminB12 === 'optimal' &&
    (s.folateSerum === 'optimal' || s.folateRBC === 'optimal')
  ) {
    out.push(ONE_CARBON.MACROCYTIC_NO_B_CAUSE);
  }

  return out.sort((a, b) => a - b);
}

/**
 * Readings that cannot be settled because a marker is MISSING, each naming the
 * one marker that would settle it.
 *
 * THE RESTRAINT RULE: a question fires only when the RESULT makes the missing
 * marker matter. A low B12 with no homocysteine gets no question, because the
 * reading is already clear. Without that rule this degrades into a list of
 * everything that was not ordered, which reads as an upsell and gets ignored.
 */
export function resolveOpenQuestions(s: NutrientMarkerStates): number[] {
  const out: number[] = [];
  const hcyAbsent = s.homocysteine === 'absent';
  const hcyHigh = s.homocysteine === 'high';

  /*
   * Q2.2 — enlarged cells with B12 not obviously the cause and no way to check
   * whether it is reaching cells. The strongest nudge in the family, so it also
   * takes precedence over the plainer Q2.1 below.
   */
  const macrocyticUnassessed =
    s.mcv === 'high' &&
    (s.vitaminB12 === 'optimal' || s.vitaminB12 === 'absent') &&
    hcyAbsent;

  // Q2.1 — B12 measured and fine, but nothing says whether it is being used.
  if (s.vitaminB12 === 'optimal' && hcyAbsent && !macrocyticUnassessed) {
    out.push(OPEN_QUESTION.B12_USE_UNASSESSED);
  }
  if (macrocyticUnassessed) out.push(OPEN_QUESTION.MACROCYTIC_B12_USE_UNASSESSED);

  // Q2.3 — B6 is the one clearing cofactor not on the panel.
  if (hcyHigh && s.vitaminB6 === 'absent') out.push(OPEN_QUESTION.B6_NOT_MEASURED);

  // Q2.4 — serum folate high is a recent-intake reading until RBC folate says
  // otherwise.
  if (s.folateSerum === 'high' && s.folateRBC === 'absent') {
    out.push(OPEN_QUESTION.RBC_FOLATE_NOT_MEASURED);
  }

  // Q2.5 — homocysteine also rises when filtration falls, and that is the
  // cheapest thing to rule out before pursuing methylation genetics.
  if (hcyHigh && s.eGFR === 'absent') out.push(OPEN_QUESTION.EGFR_NOT_MEASURED);

  // Q2.6 — B12 without folate. The two share one pathway and move together, so
  // treating either without the other is guesswork.
  if (
    measured(s.vitaminB12) &&
    s.folateSerum === 'absent' &&
    s.folateRBC === 'absent'
  ) {
    out.push(OPEN_QUESTION.FOLATE_NOT_MEASURED);
  }

  return out.sort((a, b) => a - b);
}
